import { useEffect, useState } from 'react';
import { UserCircle } from 'lucide-react';
import { useAuthedApi } from '../../lib/useAuthedApi';

type Me = {
  id: string;
  name: string;
  email: string;
  slug: string;
  job_title: string | null;
  bio: string | null;
  phone: string | null;
};

export default function Profile() {
  const apiFn = useAuthedApi();
  const [me, setMe] = useState<Me | null>(null);
  const [profile, setProfile] = useState({ name: '', job_title: '', bio: '', phone: '' });
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    apiFn<Me>('/staff/me')
      .then((m) => {
        setMe(m);
        setProfile({
          name: m.name ?? '',
          job_title: m.job_title ?? '',
          bio: m.bio ?? '',
          phone: m.phone ?? '',
        });
      })
      .catch((e) => setErr((e as Error).message));
  }, [apiFn]);

  async function save() {
    if (!me) return;
    if (!profile.name.trim()) {
      setErr('Name is required.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const updated = await apiFn<Me>(`/staff/${me.id}`, {
        method: 'PATCH',
        body: {
          name: profile.name.trim(),
          job_title: profile.job_title.trim() || null,
          bio: profile.bio.trim() || null,
          phone: profile.phone.trim() || null,
        },
      });
      setMe(updated);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (!me) return err ? <p className="err">{err}</p> : <p className="muted">Loading…</p>;

  // Heuristic: if the saved name matches the local-part of the email, the
  // webhook fell back to email.split('@')[0] because Clerk didn't have a
  // first/last name. Nudge the user to set their real name.
  const emailLocal = me.email?.split('@')[0] ?? '';
  const looksEmailDerived =
    emailLocal.length > 0 && me.name.toLowerCase() === emailLocal.toLowerCase();

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>
        <UserCircle size={16} /> Your profile
      </h2>
      <p className="muted">
        Bookees see your name and job title on the staff picker. Keep them current.
      </p>
      {looksEmailDerived && (
        <p className="warn-text">
          Your name looks like it was auto-filled from your email. Set your real name so
          bookees see something friendlier.
        </p>
      )}
      <div className="grid-2">
        <label>
          <span>Name</span>
          <input
            value={profile.name}
            onChange={(e) => setProfile({ ...profile, name: e.target.value })}
            placeholder="Jane Doe"
          />
        </label>
        <label>
          <span>Job title</span>
          <input
            value={profile.job_title}
            onChange={(e) => setProfile({ ...profile, job_title: e.target.value })}
            placeholder="Senior Stylist"
          />
        </label>
      </div>
      <label>
        <span>Bio (optional)</span>
        <textarea
          rows={3}
          value={profile.bio}
          onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
          placeholder="A sentence or two about yourself shown under your name on the booking page."
        />
      </label>
      <label>
        <span>Phone (optional)</span>
        <input
          type="tel"
          value={profile.phone}
          onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
        />
      </label>
      {err && <p className="err">{err}</p>}
      <div className="row" style={{ marginTop: '0.5rem' }}>
        {savedAt && <span className="muted small">Saved {savedAt}</span>}
        <div className="spacer" />
        <button className="primary" onClick={save} disabled={saving || !profile.name.trim()}>
          {saving ? 'Saving…' : 'Save profile'}
        </button>
      </div>
    </div>
  );
}
